import { useTranslation } from "react-i18next";
import { getCustomScheduleDateColumns } from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { useEmployeeSearchColumns } from "@/queries/employees/use-employees.query.ts";
import {
  ApiWorkflowBlockDelayReference,
  ApiWorkflowTriggerScheduleDayType,
  ApiWorkflowTriggerScheduleEntityField,
  ApiWorkflowTriggerScheduleEntityType,
  ApiWorkflowTriggerScheduleTimezoneSource,
  ApiWorkflowTriggerType,
  getApiWorkflowEventTypesTitle,
} from "@/types/workflows/workflow.base-enum.types.ts";
import {
  ApiWorkflowBlock,
  ApiWorkflowListItem,
  ApiWorkflowTriggerAbstract,
  ApiWorkflowTriggerScheduleRule,
} from "@/types/workflows/workflow.types.ts";
import { ApiWorkflowBlockTimingFields } from "@/types/workflows/workflow.action-notification.types.ts";
import { Avatar, FontAwesomeIcon, Tag } from "@skailer-com/uikit";
import { RRule, Weekday } from "rrule";
import * as yup from "yup";
import useFormatter from "@/hooks/use-formatter.hook.ts";

export type WorkflowActionTimingFormFields = ApiWorkflowBlockTimingFields;

export const getWorkflowActionTimingValues = (
  block: ApiWorkflowBlock
): ApiWorkflowBlockTimingFields => ({
  delay_days: block.delay_days,
  delay_hours: block.delay_hours,
  delay_minutes: block.delay_minutes ?? 0,
  delay_reference: block.delay_reference ?? ApiWorkflowBlockDelayReference.previous_block,
  delay_day_type: block.delay_day_type ?? ApiWorkflowTriggerScheduleDayType.calendar,
});

export const getWorkflowActionTimingPayload = (
  values: ApiWorkflowBlockTimingFields
): ApiWorkflowBlockTimingFields => ({
  delay_days: values.delay_days,
  delay_hours: values.delay_hours,
  delay_minutes: values.delay_minutes,
  delay_reference: values.delay_reference,
  delay_day_type: values.delay_day_type,
});

export const useWorkflows = () => {
  const { t } = useTranslation();
  const { formatMonthName, renderDate } = useFormatter();
  const { data: employeeColumns } = useEmployeeSearchColumns();

  const requiredDelayNumberSchema = () =>
    yup
      .number()
      .transform((value, originalValue) => (originalValue === "" ? undefined : value))
      .required(t("errors:field_required"))
      .typeError(t("errors:field_number_required"))
      .min(0, t("errors:field_number_required"));

  const workflowActionTimingSchemaFields = {
    delay_days: requiredDelayNumberSchema(),
    delay_hours: requiredDelayNumberSchema(),
    delay_minutes: requiredDelayNumberSchema(),
    delay_reference: yup
      .mixed<ApiWorkflowBlockDelayReference>()
      .oneOf(Object.values(ApiWorkflowBlockDelayReference))
      .required(t("errors:field_required")),
    delay_day_type: yup
      .mixed<ApiWorkflowTriggerScheduleDayType>()
      .oneOf(Object.values(ApiWorkflowTriggerScheduleDayType))
      .required(t("errors:field_required")),
  };

  const formatWorkflowBlockDuration = (
    days: number,
    hours: number,
    minutes: number,
    dayType: ApiWorkflowTriggerScheduleDayType
  ) => {
    const parts: string[] = [];
    if (days) {
      parts.push(
        t(
          dayType === ApiWorkflowTriggerScheduleDayType.working
            ? "plurals:working_days"
            : "plurals:calendar_days",
          { count: Math.abs(days) }
        )
      );
    }
    if (hours) parts.push(t("plurals:plural_hours", { count: Math.abs(hours) }));
    if (minutes) parts.push(t("plurals:plural_minutes", { count: Math.abs(minutes) }));
    return parts.join(" ") || t("workflow:block_delay_without_delay");
  };

  const getCustomScheduleFieldTitle = (rule: ApiWorkflowTriggerScheduleRule) =>
    getCustomScheduleDateColumns(rule.entity_field, employeeColumns).find(
      (column) => column.field_id === rule.entity_field_id
    )?.title;

  const getWorkflowBlockTimingDescription = (
    block: ApiWorkflowBlock,
    scheduleRule: ApiWorkflowTriggerScheduleRule | null,
    isRootLevelBlock = false
  ): string => {
    const reference = block.delay_reference ?? ApiWorkflowBlockDelayReference.previous_block;
    const dayType = block.delay_day_type ?? ApiWorkflowTriggerScheduleDayType.calendar;

    if (reference === ApiWorkflowBlockDelayReference.previous_block || !scheduleRule) {
      if (block.delay_days === 0 && block.delay_hours === 0 && block.delay_minutes === 0) {
        return t(
          isRootLevelBlock
            ? "workflow:block_delay_immediately_after_trigger_summary"
            : "workflow:block_delay_immediately_after_previous_block_summary"
        );
      }
      return t(
        isRootLevelBlock
          ? "workflow:block_delay_after_trigger_summary"
          : "workflow:block_delay_after_previous_block_summary",
        {
          offset: formatWorkflowBlockDuration(
            block.delay_days,
            block.delay_hours,
            block.delay_minutes,
            dayType
          ),
        }
      );
    }

    if (scheduleRule.day_type !== dayType && block.delay_days > 0) {
      return t("workflow:block_delay_after_scheduled_trigger_summary", {
        offset: formatWorkflowBlockDuration(
          block.delay_days,
          block.delay_hours,
          block.delay_minutes,
          dayType
        ),
      });
    }

    const totalDays = scheduleRule.offset_days + block.delay_days;
    const totalHours = scheduleRule.offset_hours + block.delay_hours;
    const offset = formatWorkflowBlockDuration(totalDays, totalHours, block.delay_minutes, dayType);
    const customFieldTitle = getCustomScheduleFieldTitle(scheduleRule);
    const event = customFieldTitle
      ? t("workflow:block_delay_event_custom_date", { title: customFieldTitle })
      : t(`workflow:block_delay_event_${scheduleRule.entity_field}`);

    if (totalDays === 0 && totalHours === 0 && block.delay_minutes === 0) {
      return t("workflow:block_delay_on_event_day_summary", {
        hour: scheduleRule.trigger_hour_local,
        event,
      });
    }

    return t(
      totalDays * 24 + totalHours < 0
        ? "workflow:block_delay_before_event_summary"
        : "workflow:block_delay_after_event_summary",
      { offset, event }
    );
  };

  const getWorkflowTriggerScheduleDirectionOptions = () => [
    {
      id: "before",
      title: t("common:date_before"),
    },
    {
      id: "after",
      title: t("common:date_after"),
    },
  ];

  const getWorkflowTriggerScheduleDayTypeOptions = () => [
    {
      id: ApiWorkflowTriggerScheduleDayType.calendar,
      title: t("core:calendar_days"),
    },
    {
      id: ApiWorkflowTriggerScheduleDayType.working,
      title: t("core:working_days"),
    },
  ];

  const getWorkflowTriggerScheduleTimezoneSourceOptions = () => [
    {
      id: ApiWorkflowTriggerScheduleTimezoneSource.employee,
      title: t("workflow:trigger_schedule_timezone_employee"),
    },
    {
      id: ApiWorkflowTriggerScheduleTimezoneSource.organization,
      title: t("workflow:trigger_schedule_timezone_organization"),
    },
    {
      id: ApiWorkflowTriggerScheduleTimezoneSource.custom,
      title: t("workflow:trigger_schedule_timezone_custom"),
    },
  ];

  const getWorkflowTriggerTypeIcon = (triggerType: ApiWorkflowTriggerType) => {
    switch (triggerType) {
      case ApiWorkflowTriggerType.event:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-rocket"} />} />;

      case ApiWorkflowTriggerType.manual:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-hand-pointer"} />} />;

      case ApiWorkflowTriggerType.scheduled:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-clock"} />} />;

      case ApiWorkflowTriggerType.recurring:
        return <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-rotate"} />} />;
    }
  };

  const getWorkflowStatusTitle = (triggerType: ApiWorkflowTriggerType, isActive: boolean) => {
    if (triggerType == ApiWorkflowTriggerType.manual) {
      return isActive
        ? t("workflow:workflow_trigger_type_manual")
        : t("workflow:status_is_inactive");
    }

    if (isActive) {
      return t("workflow:status_is_active");
    } else {
      return t("workflow:status_is_inactive");
    }
  };

  const getWorkflowStatusTag = (workflow: ApiWorkflowListItem) => {
    if (workflow.trigger_type == ApiWorkflowTriggerType.manual) {
      const isActive = workflow.is_active;
      return (
        <Tag variant={isActive ? "secondary" : "danger"}>
          {isActive ? t("workflow:workflow_trigger_type_manual") : t("workflow:status_is_inactive")}
        </Tag>
      );
    }

    if (workflow.is_active) {
      return <Tag variant={"positive"}>{t("workflow:status_is_active")}</Tag>;
    } else {
      return <Tag variant={"danger"}>{t("workflow:status_is_inactive")}</Tag>;
    }
  };

  const getRecurringTriggerDescription = (workflow: ApiWorkflowTriggerAbstract): string => {
    const recurringRule = workflow.trigger_recurring_rule;
    if (!recurringRule) return t("workflow:trigger_recurring_schedule_not_configured");

    try {
      const options = RRule.fromString(recurringRule.rrule).origOptions;
      const interval = options.interval ?? 1;
      const byWeekday = Array.isArray(options.byweekday)
        ? options.byweekday
        : options.byweekday != null
          ? [options.byweekday]
          : [];
      const weekdays = byWeekday.map((weekday) => {
        if (weekday instanceof Weekday) return weekday;
        if (typeof weekday === "number") return new Weekday(weekday);
        return RRule[weekday];
      });
      const weekDayCodes = ["mo", "tu", "we", "th", "fr", "sa", "su"];
      const weekDayTranslationKeys = [
        "common:monday",
        "common:tuesday",
        "common:wednesday",
        "common:thursday",
        "common:friday",
        "common:saturday",
        "common:sunday",
      ];
      const joinTitles = (titles: string[]) => {
        if (titles.length < 2) return titles[0] ?? "";
        return `${titles.slice(0, -1).join(", ")} ${t("common:and")} ${titles.at(-1)}`;
      };
      const getIntervalTitle = (
        count: number,
        unitKey: "plural_day" | "plural_month" | "plural_week" | "plural_year",
        gender: "masculine" | "feminine"
      ) => `${t(`plurals:every_${gender}`, { count })} ${t(`plurals:${unitKey}`, { count })}`;
      const monthDay = Array.isArray(options.bymonthday)
        ? options.bymonthday[0]
        : options.bymonthday;
      const month = Array.isArray(options.bymonth) ? options.bymonth[0] : options.bymonth;
      const ordinalWeekday = weekdays.find((weekday) => weekday.n != null);
      let schedule: string;

      switch (options.freq) {
        case RRule.DAILY:
          schedule =
            interval === 1
              ? t("workflow:trigger_recurring_description_daily")
              : getIntervalTitle(interval, "plural_day", "masculine");
          break;
        case RRule.WEEKLY: {
          const days = joinTitles(
            weekdays.map((weekday) =>
              t(`workflow:trigger_recurring_weekday_recurring_${weekDayCodes[weekday.weekday]}`)
            )
          );
          schedule = t("workflow:trigger_recurring_description_weekly", {
            interval:
              interval === 1
                ? t("workflow:trigger_recurring_every_week")
                : getIntervalTitle(interval, "plural_week", "feminine"),
            days,
          });
          break;
        }
        case RRule.MONTHLY:
          if (ordinalWeekday) {
            schedule = t("workflow:trigger_recurring_description_monthly_ordinal", {
              interval:
                interval === 1
                  ? t("workflow:trigger_recurring_every_month")
                  : getIntervalTitle(interval, "plural_month", "masculine"),
              ordinal: t(
                `workflow:trigger_recurring_ordinal_${ordinalWeekday.n === -1 ? "last" : ordinalWeekday.n}`
              ).toLocaleLowerCase(),
              weekday: t(weekDayTranslationKeys[ordinalWeekday.weekday]).toLocaleLowerCase(),
            });
          } else {
            schedule = t("workflow:trigger_recurring_description_monthly_day", {
              interval:
                interval === 1
                  ? t("workflow:trigger_recurring_every_month")
                  : getIntervalTitle(interval, "plural_month", "masculine"),
              day: monthDay,
            });
          }
          break;
        case RRule.YEARLY: {
          const monthTitle = month
            ? formatMonthName(new Date(2020, month - 1, 1)).toLocaleLowerCase()
            : "";
          const intervalTitle =
            interval === 1
              ? t("workflow:trigger_recurring_every_year")
              : getIntervalTitle(interval, "plural_year", "masculine");
          if (ordinalWeekday) {
            schedule = t("workflow:trigger_recurring_description_yearly_ordinal", {
              interval: intervalTitle,
              month: monthTitle,
              ordinal: t(
                `workflow:trigger_recurring_ordinal_${ordinalWeekday.n === -1 ? "last" : ordinalWeekday.n}`
              ).toLocaleLowerCase(),
              weekday: t(weekDayTranslationKeys[ordinalWeekday.weekday]).toLocaleLowerCase(),
            });
          } else {
            schedule = t("workflow:trigger_recurring_description_yearly_day", {
              interval: intervalTitle,
              month: monthTitle,
              day: monthDay,
            });
          }
          break;
        }
        default:
          return t("workflow:trigger_recurring_schedule_not_configured");
      }

      const details = [
        schedule,
        t("workflow:trigger_recurring_description_at", {
          time: `${String(recurringRule.hour).padStart(2, "0")}:00`,
        }),
        t("workflow:trigger_recurring_description_starting", {
          date: renderDate(recurringRule.date_start),
        }),
      ];

      if (options.until) {
        const until = new Date(
          options.until.getUTCFullYear(),
          options.until.getUTCMonth(),
          options.until.getUTCDate()
        );
        details.push(
          t("workflow:trigger_recurring_description_until", { date: renderDate(until) })
        );
      } else if (options.count) {
        details.push(t("plurals:workflow_recurring_runs", { count: options.count }));
      }

      return details.join(" · ");
    } catch {
      return t("workflow:trigger_recurring_schedule_not_configured");
    }
  };

  const getWorkflowTriggerDescription = (workflow: ApiWorkflowTriggerAbstract) => {
    const isRecurring =
      workflow.trigger_recurring_rule !== null ||
      ("trigger_type" in workflow && workflow.trigger_type === ApiWorkflowTriggerType.recurring);

    if (isRecurring) {
      return getRecurringTriggerDescription(workflow);
    } else if (workflow.trigger_event) {
      return getApiWorkflowEventTypesTitle(workflow.trigger_event.event_type);
    } else if (workflow.trigger_schedule_rule) {
      const rule = workflow.trigger_schedule_rule;
      let field = "";
      switch (rule.entity_field) {
        case ApiWorkflowTriggerScheduleEntityField.date_start:
          field = t("workflow:trigger_schedule_entity_field_date_start");
          break;
        case ApiWorkflowTriggerScheduleEntityField.hire_date:
          field = t("workflow:trigger_schedule_entity_field_hire_date");
          break;
        case ApiWorkflowTriggerScheduleEntityField.probation_ends_date:
          field = t("workflow:trigger_schedule_entity_field_probation_ends_date");
          break;
        case ApiWorkflowTriggerScheduleEntityField.terminate_date:
          field = t("workflow:trigger_schedule_entity_field_terminate_date");
          break;
        case ApiWorkflowTriggerScheduleEntityField.birthday:
          field = t("workflow:trigger_schedule_entity_field_birthday");
          break;
        case ApiWorkflowTriggerScheduleEntityField.work_anniversary:
          field = t("workflow:trigger_schedule_entity_field_work_anniversary");
          break;
        case ApiWorkflowTriggerScheduleEntityField.custom_field:
        case ApiWorkflowTriggerScheduleEntityField.custom_table_column:
          field =
            getCustomScheduleFieldTitle(rule) ??
            t(`workflow:trigger_schedule_entity_field_${rule.entity_field}`);
          break;
      }

      let entity = "";
      switch (rule.entity_type) {
        case ApiWorkflowTriggerScheduleEntityType.time_off:
          entity = t("workflow:trigger_schedule_entity_type_time_off");
          break;
        case ApiWorkflowTriggerScheduleEntityType.employee:
        case ApiWorkflowTriggerScheduleEntityType.custom_table_row:
          entity = t("workflow:trigger_schedule_entity_type_employee");
          break;
      }

      const offsetParts: string[] = [];
      const absDays = Math.abs(rule.offset_days);
      const absHours = Math.abs(rule.offset_hours);

      if (absDays > 0) {
        const dayType = rule.day_type ?? ApiWorkflowTriggerScheduleDayType.calendar;
        offsetParts.push(
          t(
            dayType === ApiWorkflowTriggerScheduleDayType.working
              ? "plurals:working_days"
              : "plurals:calendar_days",
            { count: absDays }
          )
        );
      }
      if (absHours > 0) {
        offsetParts.push(t("plurals:plural_hours", { count: absHours }));
      }
      const totalOffset = rule.offset_days * 24 + rule.offset_hours;
      let schedule: string;
      if (totalOffset === 0) {
        schedule = t("workflow:trigger_schedule_template_at", {
          field,
          entity,
        });
      } else {
        const offset = offsetParts.join(" ");
        const template =
          totalOffset < 0
            ? "workflow:trigger_schedule_template_before"
            : "workflow:trigger_schedule_template_after";

        schedule = t(template, {
          offset,
          field,
          entity,
        });
      }

      const timingOptions = {
        schedule,
        hour: rule.trigger_hour_local ?? 9,
        timezone: rule.timezone_name ?? "",
      };

      switch (rule.timezone_source ?? ApiWorkflowTriggerScheduleTimezoneSource.organization) {
        case ApiWorkflowTriggerScheduleTimezoneSource.employee:
          return t("workflow:trigger_schedule_summary_employee_timezone", timingOptions);
        case ApiWorkflowTriggerScheduleTimezoneSource.custom:
          return t("workflow:trigger_schedule_summary_custom_timezone", timingOptions);
        case ApiWorkflowTriggerScheduleTimezoneSource.organization:
          return t("workflow:trigger_schedule_summary_organization_timezone", timingOptions);
      }
    }
    return "-";
  };

  return {
    getWorkflowTriggerScheduleDirectionOptions,
    getWorkflowTriggerScheduleDayTypeOptions,
    getWorkflowTriggerScheduleTimezoneSourceOptions,
    getWorkflowTriggerTypeIcon,
    getWorkflowTriggerDescription,
    getWorkflowStatusTag,
    getWorkflowStatusTitle,
    getWorkflowActionTimingValues,
    getWorkflowActionTimingPayload,
    workflowActionTimingSchemaFields,
    getWorkflowBlockTimingDescription,
  };
};
